import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import Headline from "../Components/Elements/Headline";
import { IncomeTable, NeedsTable, WantsTable, SavingsInvestmentsTable, BudgetAllocationTable, BudgetDonutChart } from "../Components/Tables";
import { useLanguage } from "../context/LanguageContext";
import { useContext, useState, useEffect, useMemo, useRef } from "react";
import { AuthContext, YearContext } from "../context/context";
import { useToast } from "../context/ToastContext";
import { createOrUpdateFinanceData, updateMonthFinanceData, fetchFinanceData } from "../api";
import Dropdown from "../Components/Elements/Dropdown";
import Popup from "../Components/Elements/Popup";
import IconCircle from "../Components/Elements/IconCircle";
import { TbChevronDown, TbCalendarMonth, TbDotsVertical, TbPlus } from "react-icons/tb";
import { GrMoney } from "react-icons/gr";
import Lottie from "lottie-react";
import loadingAnimation from "../Animations/load-animation.json";
import StartMessage from "../Components/Elements/StartMessage";
// import GoalMetricCard from "../Components/Cards/CardGoalMetric";
// import { getCurrencySymbol, formatCurrency } from "../utils/stringUtils";

export default function Finance() {
  const { copy } = useLanguage();
  const { userData, setUserData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);
  const { showToast } = useToast();

  // Helper function to format metric card numbers
  // const formatMetricNumber = (amount, currency) => {
  //   const numAmount = Number(amount) || 0;
  //   const formatted = formatCurrency(numAmount);
  //   const symbol = getCurrencySymbol(currency);

  //   // Check if currency symbol goes before or after
  //   const currencyConfig = {
  //     'USD': 'before', 'GBP': 'before', 'CAD': 'before', 'AUD': 'before', 'JPY': 'before'
  //   };
  //   const position = currencyConfig[currency] || 'after';

  //   // Show everything in same size on one line for all currencies
  //   if (position === 'before') {
  //     return `${symbol}${formatted}`;
  //   } else {
  //     return `${formatted} ${symbol}`;
  //   }
  // };

  // Helper functions for user-specific localStorage keys
  const getUserSpecificKey = (key) => {
    return userData?.id ? `${key}_user_${userData.id}` : key;
  };

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const savedMonth = localStorage.getItem(getUserSpecificKey("selectedMonth"));
    if (savedMonth) return savedMonth;

    // Default to current month
    const currentMonthIndex = new Date().getMonth();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[currentMonthIndex];
  });
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const savedCurrency = localStorage.getItem(getUserSpecificKey("selectedCurrency"));
    return savedCurrency || null;
  });
  // Helper functions for finance data caching (moved up to use in initialization)
  const getFinanceCacheKey = (year) => {
    return userData?.id ? `finance_cache_${userData.id}_${year}` : null;
  };

  const getCachedFinanceDataSync = (year) => {
    try {
      const cacheKey = getFinanceCacheKey(year);
      if (!cacheKey) return null;

      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) return null;

      const parsedData = JSON.parse(cachedData);
      const now = new Date().getTime();
      const cacheExpiry = 60 * 60 * 1000; // 1 hour

      if (now - parsedData.timestamp < cacheExpiry) {
        return parsedData.data;
      } else {
        localStorage.removeItem(cacheKey);
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const [loading, setLoading] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("income"); // For mobile tabs
  const [isGoalEditOpen, setIsGoalEditOpen] = useState(false);
  const [isAddIncomeOpen, setIsAddIncomeOpen] = useState(false);
  const [newIncome, setNewIncome] = useState({ category: '', amount: '' });
  const [goalPercentages, setGoalPercentages] = useState({
    needsPercentage: 50,
    savingsPercentage: 20,
    wantsPercentage: 30
  });

  // Initialize finance data from cache if available to prevent flash of empty data
  const [financeData, setFinanceData] = useState(() => {
    if (selectedYear && userData?.id) {
      const cached = getCachedFinanceDataSync(selectedYear);
      return cached || [];
    }
    return [];
  });

  const financeDataRef = useRef(
    selectedYear && userData?.id
      ? (getCachedFinanceDataSync(selectedYear) || [])
      : []
  );

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const allCurrencies = userData?.currencies || [
    "EUR", "USD", "PLN", "DKK", "GBP", "CAD", "AUD", "CHF", "JPY", "SEK", "NOK"
  ];
  
  // Create stable default row IDs based on year, month, and category
  const createDefaultRowId = (year, month, category, index) => {
    return `default-${year}-${month}-${category}-${index}`;
  };

  // Store default data per month to keep stable references
  const defaultDataCache = useRef({});

  // Get current month data from local financeData state
  const currentMonthData = useMemo(() => {
    if (!selectedYear || !selectedMonth) return null;

    const monthNumber = months.indexOf(selectedMonth) + 1;
    const existingData = financeData.find(f => f.year === selectedYear && f.month === monthNumber);

    // If no data exists, return a default structure with stable IDs
    if (!existingData) {
      const cacheKey = `${selectedYear}-${monthNumber}`;

      // Return cached default data if it exists to maintain stable reference
      if (defaultDataCache.current[cacheKey]) {
        return defaultDataCache.current[cacheKey];
      }

      // Create new default data and cache it
      const defaultData = {
        id: null,
        user_id: userData?.id,
        year: selectedYear,
        month: monthNumber,
        currency: selectedCurrency || "EUR",
        totalIncome: 0,
        income: [{ category: '', amount: 0, id: createDefaultRowId(selectedYear, monthNumber, 'income', 0) }],
        totalNeeds: 0,
        needs: [{ category: '', amount: 0, id: createDefaultRowId(selectedYear, monthNumber, 'needs', 0) }],
        totalSavings: 0,
        savings: [{ category: '', amount: 0, id: createDefaultRowId(selectedYear, monthNumber, 'savings', 0) }],
        totalWants: 0,
        wants: [{ category: '', amount: 0, id: createDefaultRowId(selectedYear, monthNumber, 'wants', 0) }],
        budgetGoals: {
          needsPercentage: 50,
          savingsPercentage: 20,
          wantsPercentage: 30
        }
      };

      defaultDataCache.current[cacheKey] = defaultData;
      return defaultData;
    }

    // Clear cached default data for this month since we have real data
    const cacheKey = `${selectedYear}-${monthNumber}`;
    if (defaultDataCache.current[cacheKey]) {
      delete defaultDataCache.current[cacheKey];
    }

    return existingData;
  }, [selectedYear, selectedMonth, financeData, userData?.id, selectedCurrency]);


  const setCachedFinanceData = (year, data) => {
    try {
      const cacheKey = getFinanceCacheKey(year);
      if (!cacheKey) return;

      const cacheData = {
        data,
        timestamp: new Date().getTime()
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Silently fail
    }
  };

  const clearCachedFinanceData = (year) => {
    try {
      const cacheKey = getFinanceCacheKey(year);
      if (cacheKey) {
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Initialize from cache when userData becomes available
  useEffect(() => {
    if (userData?.id && selectedYear && financeData.length === 0) {
      const cached = getCachedFinanceDataSync(selectedYear);
      if (cached && cached.length > 0) {
        setFinanceData(cached);
        financeDataRef.current = cached;
      }
    }
  }, [userData?.id, selectedYear]);

  // Fetch finance data when component mounts or when selectedYear changes
  useEffect(() => {
    const fetchData = async () => {
      // Wait for selectedYear to be set
      if (!selectedYear) {
        setLoading(false);
        return;
      }

      // If userData.id is not available yet, wait for it to load
      // This prevents early fetching before auth is complete
      if (!userData?.id) {
        setLoading(true);
        return;
      }

      // Check if we already have data for this year using ref
      const hasYearData = financeDataRef.current.length > 0 &&
        financeDataRef.current.some(f => f.year === selectedYear);

      // If we have year data AND state is synced, skip fetch
      if (hasYearData && financeData.length > 0) {
        setLoading(false);
        return;
      }

      // If ref has data but state doesn't, sync them
      if (hasYearData && financeData.length === 0) {
        setFinanceData(financeDataRef.current);
        setLoading(false);
        return;
      }

      // First, try to get data from cache
      const cachedData = getCachedFinanceDataSync(selectedYear);
      if (cachedData) {
        setFinanceData(cachedData);
        financeDataRef.current = cachedData;
        setUserData(prev => ({
          ...prev,
          finance: cachedData
        }));
        setLoading(false);
        return;
      }

      // No cached data - show loading state while fetching
      setLoading(true);
      setFinanceData([]);
      financeDataRef.current = [];

      try {
        const response = await fetchFinanceData(selectedYear);

        if (response && response.data) {
          // Update local finance data state
          setFinanceData(response.data);
          financeDataRef.current = response.data;
          // Cache the data
          setCachedFinanceData(selectedYear, response.data);
          // Also update userData context for consistency
          setUserData(prev => ({
            ...prev,
            finance: response.data
          }));
        } else {
          // Initialize with empty array if no data
          setFinanceData([]);
          financeDataRef.current = [];
        }
      } catch (error) {
        if (!error.message?.includes('not found')) {
          showToast(`Error loading finance data: ${error.message}`, "error");
        }
        // Clear loading state even on error
        setFinanceData([]);
        financeDataRef.current = [];
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, userData?.id]);

  // Convert month name to number
  const getMonthNumber = (monthName) => {
    return months.indexOf(monthName) + 1;
  };

  // Save finance data and update both local state and userData
  const saveMonthData = async (budgetData) => {
    if (!selectedYear || !selectedMonth) return;

    setLoading(true);

    try {
      const monthNumber = getMonthNumber(selectedMonth);

      let response;
      if (currentMonthData) {
        // Merge new data with existing data
        const mergedData = {
          ...currentMonthData,
          ...budgetData,
          currency: selectedCurrency
        };

        response = await updateMonthFinanceData(selectedYear, monthNumber, mergedData);
      } else {
        // Create new data
        response = await createOrUpdateFinanceData(
          selectedYear,
          monthNumber,
          selectedCurrency,
          budgetData
        );
      }

      // Update local finance data state
      const updatedFinanceData = financeData.filter(f =>
        !(f.year === selectedYear && f.month === monthNumber)
      );
      updatedFinanceData.push(response.data);
      setFinanceData(updatedFinanceData);

      // Clear cache since data has been updated
      clearCachedFinanceData(selectedYear);

      // Cache the updated data
      setCachedFinanceData(selectedYear, updatedFinanceData);

      // Also update userData context
      setUserData(prev => ({
        ...prev,
        finance: updatedFinanceData
      }));

    } catch (err) {
      showToast(`Error saving data: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Update currency when data exists
  useEffect(() => {
    if (currentMonthData && currentMonthData.currency && !selectedCurrency) {
      setSelectedCurrency(currentMonthData.currency);
    }
  }, [currentMonthData, selectedCurrency]);

  // Save selected month to localStorage
  useEffect(() => {
    if (userData?.id) {
      localStorage.setItem(getUserSpecificKey("selectedMonth"), selectedMonth);
    }
  }, [selectedMonth, userData?.id]);

  // Save selected currency to localStorage
  useEffect(() => {
    if (userData?.id) {
      if (selectedCurrency) {
        localStorage.setItem(getUserSpecificKey("selectedCurrency"), selectedCurrency);
      } else {
        localStorage.removeItem(getUserSpecificKey("selectedCurrency"));
      }
    }
  }, [selectedCurrency, userData?.id]);

  // Sync goal percentages from currentMonthData
  useEffect(() => {
    if (currentMonthData?.budgetGoals) {
      setGoalPercentages({
        needsPercentage: currentMonthData.budgetGoals.needsPercentage || 50,
        savingsPercentage: currentMonthData.budgetGoals.savingsPercentage || 20,
        wantsPercentage: currentMonthData.budgetGoals.wantsPercentage || 30
      });
    }
  }, [currentMonthData?.budgetGoals]);

  // Handle goal percentage save
  const handleGoalSave = async () => {
    if (!selectedYear || !selectedMonth) return;

    // Validate total equals 100%
    const total = goalPercentages.needsPercentage + goalPercentages.savingsPercentage + goalPercentages.wantsPercentage;
    if (total !== 100) {
      showToast(`Goal percentages must total 100% (currently ${total}%)`, "error");
      return;
    }

    try {
      await saveMonthData({
        budgetGoals: goalPercentages
      });
      setIsGoalEditOpen(false);
      showToast("Budget goals updated successfully", "success");
    } catch (error) {
      showToast("Failed to update budget goals", "error");
    }
  };

  // Handle add income
  const handleAddIncome = async () => {
    if (!newIncome.category || !newIncome.amount) {
      showToast("Please fill in both category and amount", "error");
      return;
    }

    try {
      const currentIncome = currentMonthData?.income || [];
      const updatedIncome = [...currentIncome, {
        category: newIncome.category,
        amount: Number(newIncome.amount),
        id: `income-${Date.now()}`
      }];

      const totalIncome = updatedIncome.reduce((sum, item) => sum + Number(item.amount || 0), 0);

      await saveMonthData({
        income: updatedIncome,
        totalIncome: totalIncome
      });

      setIsAddIncomeOpen(false);
      setNewIncome({ category: '', amount: '' });
      showToast("Income added successfully", "success");
    } catch (error) {
      showToast("Failed to add income", "error");
    }
  };

  // Check if user has any goals with a year defined
  const hasGoalsWithYear = userData?.goals && userData.goals.length > 0 && userData.goals.some(goal => goal.year);

  return (
    <PageContentContainer
      selectedMonth={hasGoalsWithYear ? selectedMonth : undefined}
      selectedCurrency={hasGoalsWithYear ? selectedCurrency : undefined}
      onMonthChange={hasGoalsWithYear ? setSelectedMonth : undefined}
      onCurrencyChange={hasGoalsWithYear ? setSelectedCurrency : undefined}
      allCurrencies={hasGoalsWithYear ? allCurrencies : undefined}
      authUserData={userData}
    >
      {!loading && !hasGoalsWithYear ? (
        <StartMessage
          title={copy.start_message.headline}
          message={copy.start_message.description}
        />
      ) : (
        <>
          <Headline
            headline={copy?.finance?.headline || "Finances"}
            description={copy?.finance?.description || "Track and manage your money with ease. Use the built-in 50/30/20 budget tool."}
          />

      {/* Loading State */}
      {loading && financeData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <Lottie
            animationData={loadingAnimation}
            loop
            autoplay
            style={{ width: 200, height: 200 }}
          />
          <p className="text-center text-lg color-text-secondary">Loading your finance data...</p>
        </div>
      ) : (
        <>
          {/* MOBILE & TABLET VERSION (< 1024px) */}
          <div className="lg:hidden flex flex-col gap-3 md:gap-4">
            {/* Month & Currency Selectors */}
            <div className="flex flex-row items-center justify-start gap-4">
              {/* Month Selector */}
              <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
                <TbCalendarMonth size={24} />
                <div className="flex gap-1 items-center">
                  <span>{selectedMonth}</span>
                  <Dropdown
                    icon={TbChevronDown}
                    rotateOnOpen={true}
                    isOpen={isMonthDropdownOpen}
                    onOpenChange={setIsMonthDropdownOpen}
                    options={months.map((month) => ({
                      text: month,
                      onClick: () => {
                        setSelectedMonth(month);
                        setIsMonthDropdownOpen(false);
                      },
                    }))}
                  />
                </div>
              </div>

              {/* Currency Selector */}
              <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
                <GrMoney size={20} />
                <div className="flex gap-1 items-center">
                  <span>{selectedCurrency || "Select Currency"}</span>
                  <Dropdown
                    icon={TbChevronDown}
                    rotateOnOpen={true}
                    isOpen={isCurrencyDropdownOpen}
                    onOpenChange={setIsCurrencyDropdownOpen}
                    options={allCurrencies.map((currency) => ({
                      text: currency,
                      onClick: () => {
                        setSelectedCurrency(currency);
                        setIsCurrencyDropdownOpen(false);
                      },
                    }))}
                  />
                </div>
              </div>
            </div>

            {/* Card-based Overview Section */}
            <div className="card-base card-base-border card-base-background flex flex-col gap-3">
              {/* Title */}
              <h3 className="text-xl font-semibold px-4 pt-4">Budget {selectedMonth} {selectedYear}</h3>

              {/* Total Income and Donut Chart - Single Column Layout */}
              <div className="flex flex-col gap-1 items-start px-4">
                {/* Total Income */}
                <div className="flex flex-col gap-1 items-start w-full">
                  <p className="text-4xl font-bold">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: selectedCurrency || 'EUR',
                      minimumFractionDigits: 2,
                    }).format(currentMonthData?.totalIncome || 0)}
                  </p>
                  {(() => {
                    // Calculate from raw data, don't trust stored totals
                    const totalIncome = currentMonthData?.income
                      ? currentMonthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0)
                      : 0;

                    const needsAmount = currentMonthData?.needs
                      ? currentMonthData.needs.reduce((sum, item) => sum + Number(item.amount || 0), 0)
                      : 0;

                    const savingsAmount = currentMonthData?.savings
                      ? currentMonthData.savings.reduce((sum, item) => sum + Number(item.amount || 0), 0)
                      : 0;

                    const wantsAmount = currentMonthData?.wants
                      ? currentMonthData.wants.reduce((sum, item) => sum + Number(item.amount || 0), 0)
                      : 0;

                    const totalSpent = needsAmount + savingsAmount + wantsAmount;
                    const remaining = totalIncome - totalSpent;

                    if (totalIncome === 0) return null;

                    const formattedAmount = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: selectedCurrency || 'EUR',
                      minimumFractionDigits: 2,
                    }).format(Math.abs(remaining));

                    if (remaining > 0) {
                      return (
                        <div className="flex items-center justify-center gap-1 bg-yellow-100 pl-3 pr-3 py-1 rounded-full border border-yellow-600">
                          <p className="text-sm font-medium text-yellow-700">
                            {formattedAmount} Left to allocate
                          </p>
                        </div>
                      );
                    } else if (remaining < 0) {
                      return (
                        <div className="flex items-center justify-center gap-1 bg-red-100 pl-3 pr-3 py-1 rounded-full border border-red-600">
                          <p className="text-sm font-medium text-red-700">
                            {formattedAmount} Overspent
                          </p>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex items-center justify-center gap-1 bg-green-100 pl-3 pr-3 py-1 rounded-full border border-green-600">
                          <p className="text-sm font-medium text-green-700">
                            Perfect allocation!
                          </p>
                        </div>
                      );
                    }
                  })()}
                </div>

                {/* Budget Donut Chart */}
                <div className="flex items-center justify-start w-full">
                  <BudgetDonutChart
                    monthData={currentMonthData}
                    currency={selectedCurrency}
                    showHeader={false}
                    showCard={false}
                    size="large"
                  />
                </div>
              </div>

              {/* Horizontal Scrollable Category Cards */}
              <div className="overflow-hidden pb-4">
                <div className="flex gap-3 overflow-x-auto hide-scrollbar pl-4 pr-4">
                {/* Add Income Card */}
                <div
                  className="flex-shrink-0 card-base card-base-border bg-white p-5 flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setIsAddIncomeOpen(true)}
                >
                  <TbPlus size={28} className="text-gray-800" />
                </div>

                {/* Needs Card - Peach/Orange to match donut chart */}
                <div
                  className="min-w-[200px] flex-shrink-0 card-base p-5 flex flex-col justify-between gap-3 cursor-pointer hover:shadow-lg transition-shadow relative"
                  style={{ backgroundColor: 'rgb(252, 186, 137)' }}
                  onClick={() => setActiveTab("needs")}
                >
                  <div
                    className="absolute top-3 right-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsGoalEditOpen(true);
                    }}
                  >
                    <IconCircle icon={TbDotsVertical} />
                  </div>
                  <h4 className="text-gray-800 text-lg font-medium">Needs</h4>
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-800 text-3xl font-bold">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: selectedCurrency || 'EUR',
                        minimumFractionDigits: 0,
                      }).format(currentMonthData?.totalNeeds || 0)}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-gray-800 text-xl font-semibold">
                        {currentMonthData?.totalIncome > 0
                          ? Math.round((currentMonthData?.totalNeeds / currentMonthData?.totalIncome) * 100)
                          : 0}%
                      </p>
                      <div className="flex items-center justify-center gap-1 bg-white px-3 py-1 rounded-full card-base-border">
                        <p className="text-gray-600 text-sm">
                          {currentMonthData?.budgetGoals?.needsPercentage || 50}% goal
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Savings Card - Purple to match donut chart */}
                <div
                  className="min-w-[200px] flex-shrink-0 card-base p-5 flex flex-col justify-between gap-3 cursor-pointer hover:shadow-lg transition-shadow relative"
                  style={{ backgroundColor: 'rgb(180, 99, 235)' }}
                  onClick={() => setActiveTab("savings")}
                >
                  <div
                    className="absolute top-3 right-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsGoalEditOpen(true);
                    }}
                  >
                    <IconCircle icon={TbDotsVertical} />
                  </div>
                  <h4 className="text-white text-lg font-medium">Savings</h4>
                  <div className="flex flex-col gap-1">
                    <p className="text-white text-3xl font-bold">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: selectedCurrency || 'EUR',
                        minimumFractionDigits: 0,
                      }).format(currentMonthData?.totalSavings || 0)}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-white text-xl font-semibold">
                        {currentMonthData?.totalIncome > 0
                          ? Math.round((currentMonthData?.totalSavings / currentMonthData?.totalIncome) * 100)
                          : 0}%
                      </p>
                      <div className="flex items-center justify-center gap-1 bg-white px-3 py-1 rounded-full card-base-border">
                        <p className="text-gray-600 text-sm">
                          {currentMonthData?.budgetGoals?.savingsPercentage || 20}% goal
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wants Card - Blue to match donut chart */}
                <div
                  className="min-w-[200px] flex-shrink-0 card-base p-5 flex flex-col justify-between gap-3 cursor-pointer hover:shadow-lg transition-shadow relative"
                  style={{ backgroundColor: 'rgb(92, 195, 250)' }}
                  onClick={() => setActiveTab("wants")}
                >
                  <div
                    className="absolute top-3 right-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsGoalEditOpen(true);
                    }}
                  >
                    <IconCircle icon={TbDotsVertical} />
                  </div>
                  <h4 className="text-white text-lg font-medium">Wants</h4>
                  <div className="flex flex-col gap-1">
                    <p className="text-white text-3xl font-bold">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: selectedCurrency || 'EUR',
                        minimumFractionDigits: 0,
                      }).format(currentMonthData?.totalWants || 0)}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-white text-xl font-semibold">
                        {currentMonthData?.totalIncome > 0
                          ? Math.round((currentMonthData?.totalWants / currentMonthData?.totalIncome) * 100)
                          : 0}%
                      </p>
                      <div className="flex items-center justify-center gap-1 bg-white px-3 py-1 rounded-full card-base-border">
                        <p className="text-gray-600 text-sm">
                          {currentMonthData?.budgetGoals?.wantsPercentage || 30}% goal
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>

            {/* Detailed Table for Selected Category */}
            <div className="card-base card-base-border card-base-background p-3 flex flex-col gap-3">
              {/* Tab Buttons */}
              <div className="flex flex-row gap-2 card-base-border card-base bg-white p-1.5 overflow-x-auto overflow-y-hidden hide-scrollbar whitespace-nowrap">
                <button
                  className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "income" ? "bg-accent-light" : "hover:shadow-sm"}`}
                  onClick={() => setActiveTab("income")}
                >
                  Income
                </button>
                <button
                  className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "needs" ? "bg-accent-light" : "hover:shadow-sm"}`}
                  onClick={() => setActiveTab("needs")}
                >
                  Needs
                </button>
                <button
                  className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "savings" ? "bg-accent-light" : "hover:shadow-sm"}`}
                  onClick={() => setActiveTab("savings")}
                >
                  Savings
                </button>
                <button
                  className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "wants" ? "bg-accent-light" : "hover:shadow-sm"}`}
                  onClick={() => setActiveTab("wants")}
                >
                  Wants
                </button>
              </div>

              {/* Tables - Keep all mounted, show/hide with CSS to prevent remounting */}
              <div className={activeTab === "income" ? "block" : "hidden"}>
                <IncomeTable
                  monthData={currentMonthData}
                  currency={selectedCurrency}
                  onSave={saveMonthData}
                />
              </div>
              <div className={activeTab === "needs" ? "block" : "hidden"}>
                <NeedsTable
                  monthData={currentMonthData}
                  currency={selectedCurrency}
                  onSave={saveMonthData}
                />
              </div>
              <div className={activeTab === "savings" ? "block" : "hidden"}>
                <SavingsInvestmentsTable
                  monthData={currentMonthData}
                  currency={selectedCurrency}
                  onSave={saveMonthData}
                />
              </div>
              <div className={activeTab === "wants" ? "block" : "hidden"}>
                <WantsTable
                  monthData={currentMonthData}
                  currency={selectedCurrency}
                  onSave={saveMonthData}
                />
              </div>
            </div>

            {/* Budget Allocation Table - Hidden on mobile */}
            <div className="hidden lg:block">
              <BudgetAllocationTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                selectedMonth={selectedMonth}
                onSave={saveMonthData}
              />
            </div>

            {/* Add Income Popup */}
            <Popup
              isOpen={isAddIncomeOpen}
              onClose={() => {
                setIsAddIncomeOpen(false);
                setNewIncome({ category: '', amount: '' });
              }}
              title="Add Income"
              description="Add a new income source for this month."
              buttonSecondaryCTA="Cancel"
              onClickSecondaryLink={() => {
                setIsAddIncomeOpen(false);
                setNewIncome({ category: '', amount: '' });
              }}
              buttonPrimaryCTA="Add Income"
              onClickPrimaryLink={handleAddIncome}
            >
              <div className="flex flex-col gap-4">
                {/* Category Input */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Category</label>
                  <input
                    type="text"
                    placeholder="e.g., Salary, Freelance, etc."
                    value={newIncome.category}
                    onChange={(e) => setNewIncome({ ...newIncome, category: e.target.value })}
                    className="px-3 py-2 card-base-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Amount Input */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Amount ({selectedCurrency || 'EUR'})</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                    className="px-3 py-2 card-base-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </Popup>

            {/* Goal Edit Popup */}
            <Popup
              isOpen={isGoalEditOpen}
              onClose={() => setIsGoalEditOpen(false)}
              title="Edit Budget Goals"
              description="Adjust your monthly budget goal percentages. Total must equal 100%."
              buttonSecondaryCTA="Cancel"
              onClickSecondaryLink={() => setIsGoalEditOpen(false)}
              buttonPrimaryCTA="Save Goals"
              onClickPrimaryLink={handleGoalSave}
            >
              <div className="flex flex-col gap-4">
                {/* Needs Percentage */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(252, 186, 137)' }}></div>
                    Needs Goal (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={goalPercentages.needsPercentage}
                    onChange={(e) => setGoalPercentages({
                      ...goalPercentages,
                      needsPercentage: parseInt(e.target.value) || 0
                    })}
                    className="px-3 py-2 card-base-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Savings Percentage */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(180, 99, 235)' }}></div>
                    Savings Goal (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={goalPercentages.savingsPercentage}
                    onChange={(e) => setGoalPercentages({
                      ...goalPercentages,
                      savingsPercentage: parseInt(e.target.value) || 0
                    })}
                    className="px-3 py-2 card-base-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Wants Percentage */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(92, 195, 250)' }}></div>
                    Wants Goal (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={goalPercentages.wantsPercentage}
                    onChange={(e) => setGoalPercentages({
                      ...goalPercentages,
                      wantsPercentage: parseInt(e.target.value) || 0
                    })}
                    className="px-3 py-2 card-base-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Total */}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">Total:</span>
                  <span className={`font-semibold ${
                    (goalPercentages.needsPercentage + goalPercentages.savingsPercentage + goalPercentages.wantsPercentage) === 100
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {goalPercentages.needsPercentage + goalPercentages.savingsPercentage + goalPercentages.wantsPercentage}%
                  </span>
                </div>
              </div>
            </Popup>
          </div>

          {/* DESKTOP VERSION (>= 1024px) */}
          <div className="hidden lg:flex flex-col gap-3 md:gap-4">
            {/* Budget Overview - Allocation Table + Donut Chart */}
            <div className="grid grid-cols-[5fr_2fr] w-full gap-3 md:gap-4">
              <BudgetAllocationTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                selectedMonth={selectedMonth}
                onSave={saveMonthData}
              />
              <BudgetDonutChart
                monthData={currentMonthData}
                currency={selectedCurrency}
              />
            </div>

            {/* 4 Budget Tables Grid */}
            <div className="grid grid-cols-4 w-full gap-3 md:gap-4">
              <IncomeTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                onSave={saveMonthData}
              />
              <NeedsTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                onSave={saveMonthData}
              />
              <SavingsInvestmentsTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                onSave={saveMonthData}
              />
              <WantsTable
                monthData={currentMonthData}
                currency={selectedCurrency}
                onSave={saveMonthData}
              />
            </div>

            {/* Finance Metrics */}
            {/* <div className="grid grid-cols-3 gap-2">
              <GoalMetricCard
                title="Needs"
                number={formatMetricNumber(currentMonthData?.totalNeeds, selectedCurrency || 'EUR')}
                description="Essential expenses"
              />
              <GoalMetricCard
                title="Savings"
                number={formatMetricNumber(currentMonthData?.totalSavings, selectedCurrency || 'EUR')}
                description="Savings & Investments"
              />
              <GoalMetricCard
                title="Wants"
                number={formatMetricNumber(currentMonthData?.totalWants, selectedCurrency || 'EUR')}
                description="Discretionary spending"
              />
            </div> */}
          </div>
        </>
      )}
        </>
      )}
    </PageContentContainer>
  );
}